import React, { useState }from "react";
import styled from "styled-components";
import type { Details } from "./index";
import { format } from "date-fns";
import { TEstimate, TAdditionalTerm } from "types/estimate";
import DataLabel from "components/dataLabel";
import { NoStyleA } from "components/button";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useModalContext } from "components/modal";
import { useTranslation } from 'react-i18next';
import { enUS, es } from "date-fns/locale";
import i18n from "i18n";

type Props = {
  details: TEstimate;
};

export const dateFormatter = (date: string | null) => {
  const locale = i18n.language === "es" ? es : enUS;

  if (!date) {
    return "-";
  }
  return format(new Date(date), "MMM dd, yyyy, hh:mm aa", {
    locale: locale,
  });
};

export const Info = (props: Props) => {
  const { t } = useTranslation();
  const tBase = "views.estimate.info";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { setModal } = useModalContext();

  const [expandedTerms, setExpandedTerms] = useState(false);

  const { 
    additional_terms: additionalTerms,
    read_timestamp: opened,
    respond_date: signed,
    fk_request_parent_id: requestId,
    fk_project_id: projectId,
    fk_status_id: statusId,
    print_name: printName,
  } = props.details;

  const requestDescription = props.details.request?.description || null;

  const accepted = statusId === "5" ? true : false;
  const declined = statusId === "4" ? true : false;
  const pending = !accepted && !declined;
  const isRequest = requestId ? true : false;


  const status = (term: TAdditionalTerm) => {
    if (pending) {
      return (
        <span className="text-red-600">{tr("Pending")}...</span>
      )
    } else if (declined) {
      return (
        <span className="text-red-600">{tr("Declined")}</span>
      )
    } else {
      if (term.type === "yes_no" || term.type === "yes_no_initials") {
        return (
          <div>
            {term.accepted ? 
              <div className="text-green-600">{tr("Yes")}</div>
              : <div className="text-red-600">{tr("No")}</div>
            }
            {term.type === "yes_no_initials" && term.initial && (
              <>
                <div>
                  {term.file ? (
                    <button
                      className="border-2 hover:border-blue-400 px-2"
                      onClick={() => setModal({
                        label: "Initial",
                        component: (
                          <div className="flex justify-center items-center h-full w-full">
                            <img src={term.file} alt="initial" className="h-20" />
                          </div>
                        )
                      })}
                    >
                      <img src={term.file} alt="initial" className="h-6" />
                    </button>
                  ) : (
                    <div className="border-2">
                      {term.initial}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )
      } else if (term.type === "initials") {
        return (
          <>
            {term.initial ? 
              <div>
                {term.file ? (
                  <button
                    className="border-2 hover:border-blue-400 px-2"
                    onClick={() => setModal({
                      label: "Initial",
                      component: (
                        <div className="flex justify-center items-center h-full w-full">
                          <img src={term.file} alt="initial" className="h-20" />
                        </div>
                      )
                    })}
                  >
                    <img src={term.file} alt="initial" className="h-6" />
                  </button>
                ) : (
                  <div className="border-2">
                    {term.initial}
                  </div>
                )}
              </div> 
              : <div className="text-gray-500">{tr("None")}</div>
            }
          </>
        )
      } else if (term.type === "signature" || term.id.includes("tnc")) {
        return (
          <>
            {term.signature ? 
              <div>
                {term.file ? (
                  <>
                    <div>{printName}</div>
                    <button 
                      className="border-2 hover:border-blue-400"
                      onClick={() => setModal({
                        label: "Signature",
                        component: (
                          <>
                            <img src={term.file} alt="signature" className="h-20" />
                          </>
                        )
                      })}
                    >
                      <img src={term.file} alt="signature" className="h-6" />
                    </button>
                  </>
                ) : (
                  <div className="border-2">
                    {term.signature}
                  </div>
                )}
              </div> 
              : <div className="text-gray-500">{tr("None")}</div>
            }
          </>
        )
      } else if (term.type ==="none") {
        return (
          <span className="text-gray-500">{tr("None")}</span>
        )
      } else {
        return (
          <span className="text-red-600">{tr("Pending")}...</span>
        )
      }
    }
  };

  return (
    <div className="md:grid-cols-2 grid gap-2 space-y-2">
      <div className="order-1 mt-1">
        <DataLabel title={isRequest ? tr("Request ID") : tr("Project ID")} className="">
          {isRequest ? (
            <NoStyleA
              className="!text-blue-700 !underline"
              to={`/requests/${requestId}`}
            >
              {`${tr("Request")} #${requestId}`}
            </NoStyleA>
          ) : (
            <NoStyleA
              className="!text-blue-700 !underline"
              to={`/projects/${projectId}`}
            >
              {`${tr("Project")} #${projectId}`}
            </NoStyleA>
          )}
        </DataLabel>
        {isRequest && requestDescription && (
          <DataLabel title={tr("Request Description")} className="mt-1">
            {requestDescription}
          </DataLabel>
        )}
      </div>
      <DataLabel title={tr("Created")} className="order-2">
        {dateFormatter(props.details.entry_date)}
      </DataLabel>
      <DataLabel title={tr("Description")} className="order-3">
        {props.details.description}
      </DataLabel>
      <DataLabel title={tr("Opened")} className="order-4">
        {opened ? (
          dateFormatter(opened)
        ) : (
          <span className="text-red-600">{tr("Pending")}...</span>
        )}
      </DataLabel>
      <DataLabel title={tr("Terms and Conditions")} className="order-5">
        <button
          className="!text-blue-700 !underline mt-1.5 flex items-center space-x-1"
          onClick={() => setExpandedTerms(!expandedTerms)}
        >
          <div>{expandedTerms ? tr("Hide") : tr("View")}</div>
          {expandedTerms ? <FaChevronUp /> : <FaChevronDown />}
        </button>
      </DataLabel>
      {expandedTerms && (
        <div className="order-7 col-span-2 border border-grey-300 px-2 py-2 rounded-md bg-slate-50">
          {/* Header Row */}
          <div className="grid grid-cols-10 gap-x-5 text-gray-500 font-semibold">
            <div className="col-span-2">{tr("Label")}</div>
            <div className="col-span-6">{tr("Text")}</div>
            <div className="col-span-1">{tr("Type")}</div>
            <div className="col-span-1">{tr("Status")}</div>
          </div>
          {/* Terms and Conditions */}
          {additionalTerms && additionalTerms.length > 0 ? (
            additionalTerms
            .sort((a, b) => {
              if (a.id.includes("tnc")) return -1; // Place items with "tnc" in the `id` first
              if (b.id.includes("tnc")) return 1;  // Place items with "tnc" in the `id` first
              return 0; // Keep the rest of the order the same
            })
            .map((term, i) => (
              <div
                key={term.id}
                className="grid grid-cols-10 gap-x-5 space-y-2 text-sm"
              >
                <div className="border-b border-gray-300 col-span-10 mt-2" />
                <div className="col-span-2">{term.label}</div>
                <div className="col-span-6 whitespace-pre-wrap">
                  {term.text}
                </div>
                <div className="col-span-1">
                  {term.type === "yes_no"
                    ? tr("Yes/No")
                    : term.type === "initials"
                    ? tr("Initials")
                    : term.type === "yes_no_initials"
                    ? (
                      <div className="mt-1">
                        <div>{tr("Yes/No")}</div>
                        <div>+{tr("Initials")}</div>
                      </div>
                    )
                    : term.type === "signature" || term.id.includes("tnc")
                    ? tr("Signature")
                    : tr("None")}
                </div>
                <div className="col-span-1">
                  {status(term)}
                </div>
              </div>
            ))
          ) : (
            <div className="p-2 text-slate-400">
              {tr("None found. Please send estimate to view terms.")}
            </div>
          )}
        </div>
      )}
      <div className="order-6 flex flex-col">
        <div className="flex items-center mb-1">
          <div className="text-[#64748b] text-sm">
            {declined ? tr("Declined") : tr("Signed")}
          </div>
          {declined && (
            <button 
              className="ml-2 !text-blue-700 !underline" 
              onClick={() => setModal({
                label: "Decline Note",
                component: (
                  <>
                    <div className="text-sm text-gray-500">{props.details.declined_note}</div>
                  </>
                )
              })}
            >
              {tr("View decline note")}
            </button>
          )}
        </div>
        {signed ? (
          dateFormatter(signed)
        ) : (
          <span className="text-red-600">{tr("Pending")}...</span>
        )}
      </div>
    </div>
  );
};
